import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { gameDataActions } from '../Store/Actions/gameDataActions';
import { Portrait, StatBar, Label, Rule } from './Common/OkoylUI';
import { log } from '../Helpers/logger';

const MONSTER_PLACEHOLDER_SKIN = '/assets/monsters/monster_1.png';

// No GSAP on monster cards. The earlier fade-out implementation was the
// root cause of the "removeChild: not a child of this node" crash on
// monster death under React 18 — GSAP's inline-style mutations made the
// DOM diverge from React's reconciler view. The 600 ms saga delay before
// removeMonsterByIndex still gives the player a beat to see the 0-HP
// frame before the card unmounts.
const MonsterCard = React.memo(({ monster, index, isSelected, onSelect, fallbackName, healthLabel }) => {
    const id = `monster-${index}`;
    const skin = monster.skin || MONSTER_PLACEHOLDER_SKIN;
    const currentHealth = monster.stats?.health ?? 0;

    return (
        <label htmlFor={id} style={{ cursor: 'pointer', display: 'block' }}>
            <input
                type="radio"
                id={id}
                value={index}
                onChange={onSelect}
                checked={isSelected}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
            />
            <Portrait
                src={skin}
                name={monster.name || fallbackName}
                sub={monster.type ? `${monster.type}` : null}
                tint="neant"
                selected={isSelected}
            />
            <div style={{ marginTop: 10 }}>
                <StatBar
                    label={healthLabel}
                    value={currentHealth}
                    max={monster.stats?.maxHealth ?? 25}
                    kind="hp"
                />
            </div>
        </label>
    );
});

const FightDisplayBox = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const hero = useSelector(state => state.hero);
    const monsters = useSelector(state => state.gameData?.fightData?.monsters || []);
    const rawIndex = useSelector(state => state.gameData?.fightData?.currentMonstersIndex || 0);
    const logs = useSelector(state => state.logs || []);
    const currentMonsterIndex = Math.min(rawIndex, Math.max(0, monsters.length - 1));

    const handleMonsterSelect = useCallback((event) => {
        const { value } = event.target;
        log.ui.log('Monstre sélectionné:', value);
        dispatch(gameDataActions.setFightDataCurrentSelectedMonster(parseInt(value, 10)));
    }, [dispatch]);

    const monsterFallback = t('fight.monsterFallback');
    const healthLabel = t('fight.monsterStatLabel');
    const monstersGrid = useMemo(() => {
        if (!monsters || !monsters.length) return null;
        // Stable key per monster identity so React doesn't unmount/remount
        // surviving cards when one is removed mid-fight (avoids losing the
        // GSAP timeline and re-firing the shake on shifted indices).
        return monsters.map((monster, index) => {
            const stableKey = monster.id || `${monster.name || 'm'}-${index}`;
            return (
                <MonsterCard
                    key={stableKey}
                    monster={monster}
                    index={index}
                    isSelected={index === currentMonsterIndex}
                    onSelect={handleMonsterSelect}
                    fallbackName={monsterFallback}
                    healthLabel={healthLabel}
                />
            );
        });
    }, [monsters, currentMonsterIndex, handleMonsterSelect, monsterFallback, healthLabel]);

    const facingLabel = monsters.length > 1
        ? t('fight.facingPlural', { count: monsters.length })
        : t('fight.facing', { count: monsters.length });

    return (
        <div className="okcard okcard-bare" style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <Label color="neant">{t('fight.title')}</Label>
                <Label>{facingLabel}</Label>
            </div>

            {monsters.length > 0 ? (
                <div style={{
                    display: 'grid',
                    // Cap each monster card at 240 px so a single antagonist
                    // doesn't stretch across the whole panel; up to 3 across.
                    gridTemplateColumns: `repeat(${Math.min(monsters.length, 3)}, minmax(0, 240px))`,
                    justifyContent: 'center',
                    gap: 14,
                }}>
                    {monstersGrid}
                </div>
            ) : (
                <p className="oklore">{t('fight.noMonsters')}</p>
            )}

            <Rule />

            <Label color="gold">{t('fight.logTitle')}</Label>
            <div className="oklog" style={{ marginTop: 8 }}>
                {logs.length === 0 && (
                    <div className="oklog-line" data-kind="lore">{t('fight.logEmpty')}</div>
                )}
                {logs.slice(-10).map((entry, idx) => (
                    <div key={idx} className="oklog-line">{typeof entry === 'string' ? entry : entry}</div>
                ))}
            </div>
        </div>
    );
};

export default FightDisplayBox;
